import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from './utils';
import {
  Video,
  Bell,
  Clock,
  Plus,
  Search,
  Grid,
  User,
  LogOut,
  X,
} from 'lucide-react';
import { backendApi } from './api/backendApi';
import { Button } from './Components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './Components/ui/dropdown-menu';

import { colors as DS, TechLabel } from '../design-system';

/* ------------------------------------------------------------------ */
/*  ATS · application shell, blueprint design language               */
/* ------------------------------------------------------------------ */
const ACCENT = DS.product.ats; // indigo

export default function Layout({ children, currentPageName }) {
  const [user, setUser] = React.useState(null);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [showSearch, setShowSearch] = React.useState(false);

  React.useEffect(() => {
    backendApi.auth.me().then(setUser).catch(() => {});
  }, []);

  React.useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('#search-dropdown') && !e.target.closest('#search-btn')) {
        setShowSearch(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const navItems = [
    { name: 'Home',         page: 'Dashboard'   },
    { name: 'Job Posting',  page: 'JobPosting'  },
    { name: 'Applicants',   page: 'Applicants'  },
    { name: 'Client',       page: 'Clients'     },
    { name: 'Vendor',       page: 'Vendors'     },
    { name: 'Talent Bench', page: 'TalentBench' },
    { name: 'eBoarding',    page: 'EBoarding'   },
    { name: 'Placements',   page: 'Placements'  },
    { name: 'Tools',        page: 'Tools'       },
  ];

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{
        background: DS.paper,
        color: DS.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      <style>{`
        .ats-notif-panel {
          position: fixed;
          top: 0;
          right: 0;
          width: 380px;
          max-width: 92vw;
          height: 100vh;
          background: rgba(247,247,245,0.92);
          backdrop-filter: saturate(160%) blur(20px);
          -webkit-backdrop-filter: saturate(160%) blur(20px);
          border-left: 1px solid ${DS.hairline};
          box-shadow: -12px 0 36px -12px rgba(26,60,43,0.18);
          transform: translateX(100%);
          transition: transform 0.3s cubic-bezier(0.25,0.1,0.25,1);
          z-index: 9999;
          display: flex;
          flex-direction: column;
        }
        .ats-notif-panel.show { transform: translateX(0); }

        .ats-search-box {
          position: absolute;
          top: 46px;
          right: 0;
          width: 280px;
          background: rgba(255,255,255,0.96);
          backdrop-filter: saturate(160%) blur(16px);
          -webkit-backdrop-filter: saturate(160%) blur(16px);
          border: 1px solid ${DS.hairline};
          box-shadow: 0 16px 36px -16px rgba(26,60,43,0.22);
          padding: 12px;
          z-index: 9999;
        }
      `}</style>

      {/* TOP NAV */}
      <nav
        className="sticky top-0 z-50 border-b"
        style={{
          background: 'rgba(247,247,245,0.92)',
          backdropFilter: 'saturate(160%) blur(14px)',
          WebkitBackdropFilter: 'saturate(160%) blur(14px)',
          borderColor: DS.hairline,
        }}
      >
        <div className="px-4 py-2.5">
          <div className="flex items-center justify-between gap-2">
            {/* LOGO + module tag */}
            <Link
              to={createPageUrl('Dashboard')}
              className="flex items-center gap-3 flex-shrink-0 group"
            >
              <img
                src="/logo 2.jpg"
                alt="WebIdeal Logo"
                className="w-10 h-10 object-contain bg-white p-1 transform group-hover:scale-105 transition-transform duration-300"
                style={{ border: `1px solid ${DS.hairline}` }}
              />
              <div className="flex flex-col">
                <span className="text-base font-extrabold tracking-wide" style={{ color: DS.forest }}>
                  WEBIDEAL
                </span>
                <TechLabel style={{ color: ACCENT }}>module · ats</TechLabel>
              </div>
            </Link>

            {/* NAV ITEMS */}
            <div className="hidden md:flex items-center flex-1 justify-center gap-px" style={{ background: DS.hairline }}>
              {navItems.map((item) => {
                const active = currentPageName === item.page;
                return (
                  <Link
                    key={item.page}
                    to={createPageUrl(item.page)}
                    className="px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors duration-150"
                    style={{
                      background: active ? DS.forest : DS.paper,
                      color: active ? DS.paper : DS.grid,
                      borderTop: active ? `2px solid ${ACCENT}` : '2px solid transparent',
                    }}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* ACTION ICONS */}
            <div className="flex items-center gap-1 flex-shrink-0 relative">
              <ActionIcon ariaLabel="Video meeting">
                <Video className="h-4 w-4" />
              </ActionIcon>

              {/* Bell with mint pulse */}
              <ActionIcon
                ariaLabel="Notifications"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell className="h-4 w-4" />
                <span
                  className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: ACCENT }}
                />
              </ActionIcon>

              <ActionIcon ariaLabel="Recent">
                <Clock className="h-4 w-4" />
              </ActionIcon>

              <ActionIcon ariaLabel="New">
                <Plus className="h-4 w-4" />
              </ActionIcon>

              {/* Search */}
              <div className="relative">
                <ActionIcon
                  id="search-btn"
                  ariaLabel="Search"
                  onClick={() => setShowSearch(!showSearch)}
                >
                  <Search className="h-4 w-4" />
                </ActionIcon>
                {showSearch && (
                  <div id="search-dropdown" className="ats-search-box">
                    <input
                      type="text"
                      placeholder="Search applicants, jobs..."
                      className="ds-skeu-input w-full h-9 px-3 text-sm focus:outline-none"
                      style={{ color: DS.forest, fontFamily: 'inherit' }}
                    />
                    <div className="mt-3">
                      <TechLabel style={{ color: `${DS.grid}99` }}>suggested</TechLabel>
                      <ul className="mt-2 space-y-1">
                        {['Applicants', 'Jobs', 'Clients', 'Vendors'].map((s) => (
                          <li
                            key={s}
                            className="flex items-center gap-2 px-2 py-1 cursor-pointer text-sm transition-colors hover:bg-[#1A3C2B]/[0.05]"
                            style={{ color: DS.grid }}
                          >
                            <span className="w-1.5 h-1.5" style={{ background: ACCENT }} />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* Tools */}
              <Link to={createPageUrl('Tools')}>
                <ActionIcon ariaLabel="Tools">
                  <Grid className="h-4 w-4" />
                </ActionIcon>
              </Link>

              {/* USER MENU */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="User menu"
                    className="cursor-pointer w-9 h-9 flex items-center justify-center transition-colors duration-150"
                    style={{
                      background: DS.forest,
                      color: DS.paper,
                      border: `1px solid ${DS.forest}`,
                    }}
                  >
                    <User className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-60 ds-glass"
                  style={{ borderRadius: 0, padding: 0 }}
                >
                  <div className="px-4 py-3 border-b" style={{ borderColor: DS.hairline }}>
                    <TechLabel style={{ color: ACCENT }}>user · session</TechLabel>
                    <p className="text-sm font-bold mt-1 truncate" style={{ color: DS.forest }}>
                      {user?.full_name || 'User'}
                    </p>
                    <p className="text-xs truncate" style={{ color: DS.grid }}>
                      {user?.email}
                    </p>
                  </div>
                  <DropdownMenuSeparator className="m-0" />
                  <DropdownMenuItem
                    onClick={() => backendApi.auth.logout()}
                    className="cursor-pointer text-sm font-medium px-4 py-3"
                    style={{ color: DS.forest, borderRadius: 0 }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* NOTIFICATIONS, liquid-glass slide panel */}
      <div className={`ats-notif-panel ${showNotifications ? 'show' : ''}`}>
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: DS.hairline }}>
          <div>
            <TechLabel style={{ color: ACCENT }}>04 · live</TechLabel>
            <h2 className="mt-1 text-lg font-bold" style={{ color: DS.forest }}>
              Notifications
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setShowNotifications(false)}
            className="cursor-pointer w-8 h-8 flex items-center justify-center border transition-colors hover:bg-[#1A3C2B]/[0.04]"
            style={{ borderColor: DS.hairline, color: DS.grid }}
            aria-label="Close notifications"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-px" style={{ background: DS.hairline }}>
          {[
            { idx: '01', title: 'New applicant received', when: '2 min ago' },
            { idx: '02', title: 'Client approved submission', when: '10 min ago' },
            { idx: '03', title: 'Job updated', when: '1 hour ago' },
          ].map((n) => (
            <div
              key={n.idx}
              className="p-4 ds-spatial cursor-pointer transition-colors duration-200 hover:bg-white"
              style={{ background: DS.paper, borderLeft: `4px solid ${ACCENT}` }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <TechLabel style={{ color: ACCENT }}>{n.idx}</TechLabel>
                  <p className="text-sm font-semibold mt-1 truncate" style={{ color: DS.forest }}>
                    {n.title}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: DS.grid }}>
                    {n.when}
                  </p>
                </div>
                <span
                  className="w-2 h-2 mt-2 rounded-full animate-pulse flex-shrink-0"
                  style={{ background: ACCENT }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t flex items-center justify-between" style={{ borderColor: DS.hairline }}>
          <TechLabel style={{ color: `${DS.grid}99` }}>03 unread</TechLabel>
          <button
            type="button"
            className="text-xs font-bold uppercase transition-colors hover:text-[#1A3C2B]"
            style={{ letterSpacing: '0.18em', color: ACCENT }}
          >
            Mark all read
          </button>
        </div>
      </div>

      {/* MAIN */}
      <main className="p-6 overflow-x-hidden">{children}</main>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Square hairline action icon, replaces the rounded ghost button   */
/* ------------------------------------------------------------------ */
function ActionIcon({ children, onClick, ariaLabel, id }) {
  return (
    <Button
      id={id}
      variant="ghost"
      size="icon"
      aria-label={ariaLabel}
      onClick={onClick}
      className="w-9 h-9 relative transition-colors duration-150"
      style={{
        border: `1px solid ${DS.hairline}`,
        color: DS.grid,
        background: 'transparent',
        borderRadius: 0,
      }}
    >
      {children}
    </Button>
  );
}
